import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import AppLogo from '../../components/AppLogo'
import { shellConsumerRegisterUrl } from '../../utils/shellConsumerLoginUrl'
import { captureTrainerRefFromSearch, persistTrainerRef } from '../../utils/foodTrainerRef'
import { register as registerUser } from '../../store/authSlice'
import { api } from '../../services/api'
import { AppDispatch } from '../../store/store'

type TrainerPeek = {
  trainerCode: string
  firstName: string
  lastName: string
  gymName?: string | null
}

/**
 * Sin QR: el alta comercial sigue en el ecosistema (Wompi).
 * Con ?ref=TRN-…: formulario Nest, pending + trainer_id/gym_id.
 */
export default function RegisterPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const ref = captureTrainerRefFromSearch(location.search)
  const [peek, setPeek] = useState<TrainerPeek | null>(null)
  const [refError, setRefError] = useState('')
  const [loadingPeek, setLoadingPeek] = useState(Boolean(ref))
  const [saving, setSaving] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  })

  useEffect(() => {
    if (!ref) {
      window.location.replace(shellConsumerRegisterUrl())
      return
    }
    let cancelled = false
    setLoadingPeek(true)
    api.get(`/auth/trainer-ref/${encodeURIComponent(ref)}`)
      .then(({ data }) => {
        if (!cancelled) {
          setPeek(data)
          setRefError('')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPeek(null)
          setRefError('Este código de entrenador no es válido. Usa el registro habitual o pide un QR nuevo.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPeek(false)
      })
    return () => { cancelled = true }
  }, [ref])

  if (!ref) {
    return (
      <div className="food-plan-root min-h-screen bg-[#f9fafb] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AppLogo size={56} className="mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Registro Food Plan</h1>
          <p className="text-gray-600 text-sm mb-4">
            El alta se hace en Ecosistema Fit: primero el pago (Wompi) y queda pendiente
            hasta que el administrador de Food Plan active tu cuenta.
          </p>
          <a href={shellConsumerRegisterUrl()} className="text-primary-600 font-medium underline">
            Continuar al registro
          </a>
        </div>
      </div>
    )
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!peek) return
    if (!accepted) {
      toast.error('Acepta políticas y términos para continuar')
      return
    }
    setSaving(true)
    try {
      const result = await dispatch(registerUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        planType: 'BASIC',
        trainerCode: peek.trainerCode,
        pendingActivation: true,
        acceptedPrivacyPolicy: true,
        acceptedTerms: true,
      }))
      if (registerUser.fulfilled.match(result)) {
        persistTrainerRef(null)
        toast.success('Cuenta creada. Queda pendiente de activación.')
        navigate('/pendiente-activacion')
      } else {
        const msg = String(result.payload || 'No se pudo registrar')
        if (msg !== 'ENTRY_REDIRECT') toast.error(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="food-plan-root min-h-screen bg-[#f9fafb] text-[#111827] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <AppLogo size={48} className="mx-auto mb-3" />
        <h1 className="text-xl font-bold text-center text-gray-900">Registro Food Plan</h1>
        {loadingPeek ? (
          <p className="text-sm text-gray-500 text-center mt-4">Validando código del entrenador…</p>
        ) : refError ? (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3 space-y-2">
            <p>{refError}</p>
            <a href={shellConsumerRegisterUrl()} className="text-primary-600 font-medium underline">
              Ir al registro del ecosistema
            </a>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 text-center mt-2 mb-4">
              Te vinculas con <span className="font-semibold">{peek?.firstName} {peek?.lastName}</span>
              {peek?.gymName ? <> · {peek.gymName}</> : null}
            </p>
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className="input" required minLength={2} placeholder="Nombre" value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                <input className="input" required minLength={2} placeholder="Apellido" value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
              </div>
              <input type="email" className="input" required placeholder="Correo" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <input className="input" placeholder="Teléfono (opcional)" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              <input type="password" className="input" required minLength={6} placeholder="Contraseña (mín. 6)"
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input type="checkbox" className="mt-0.5" checked={accepted} onChange={e => setAccepted(e.target.checked)} />
                Acepto políticas de privacidad y términos de Food Plan.
              </label>
              <button type="submit" disabled={saving} className="btn-primary w-full py-3">
                {saving ? 'Creando cuenta…' : 'Crear cuenta'}
              </button>
            </form>
          </>
        )}
        <p className="text-sm text-gray-500 mt-4 text-center">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-primary-600 font-medium underline">Inicia sesión</Link>
        </p>
      </div>
    </div>
  )
}
